import { useCallback, useEffect, useRef, useState } from "react"

import { chatWithNotebook, getNotebook } from "../utils/notebooksApi"
import type { Notebook } from "../utils/notebookSnapshot"

export function useNotebookChat({
  notebook,
  organizationSlug,
  workspaceId,
  onSaved,
  onBusyChange,
  locked = false,
}: {
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  onSaved: (notebook: Notebook) => void
  onBusyChange: (busy: boolean) => void
  locked?: boolean
}) {
  const [message, setMessage] = useState("")
  const [pending, setPending] = useState("")
  const [queue, setQueue] = useState<string[]>([])
  const [error, setError] = useState("")
  const [failed, setFailed] = useState("")
  const scope = `${organizationSlug}:${workspaceId}:${notebook.id}`
  const active = useRef({ scope, generation: 0 })
  const inFlight = useRef(false)
  const latest = useRef(notebook)
  useEffect(() => {
    latest.current = notebook
  }, [notebook])
  useEffect(() => {
    active.current = { scope, generation: active.current.generation + 1 }
    return () => {
      active.current = { scope: "", generation: active.current.generation + 1 }
    }
  }, [scope])
  useEffect(() => {
    if (!queue.length && !pending) {
      return
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [queue.length, pending])
  const run = useCallback(
    async (text: string) => {
      const expected = latest.current.updated_at
      const generation = active.current.generation
      const current = () =>
        active.current.scope === scope &&
        active.current.generation === generation
      inFlight.current = true
      setPending(text)
      setError("")
      onBusyChange(true)
      try {
        const saved = await chatWithNotebook(
          organizationSlug,
          workspaceId,
          latest.current,
          text,
        )
        if (!current()) {
          return
        }
        latest.current = saved
        onSaved(saved)
      } catch (failure) {
        if (!current()) {
          return
        }
        try {
          const saved = await getNotebook(
            organizationSlug,
            workspaceId,
            notebook.id,
          )
          if (!current()) {
            return
          }
          latest.current = saved
          onSaved(saved)
          const history = saved.chat ?? []
          if (
            saved.updated_at !== expected &&
            history.at(-2)?.role === "user" &&
            history.at(-2)?.content === text &&
            history.at(-1)?.role === "assistant"
          ) {
            return
          }
        } catch {
          /* Retain the request for retry. */
        }
        setFailed(text)
        setError(
          failure instanceof Error
            ? failure.message
            : "Sofia could not update the notebook.",
        )
      } finally {
        if (current()) {
          inFlight.current = false
          setPending("")
          onBusyChange(false)
        }
      }
    },
    [notebook.id, onBusyChange, onSaved, organizationSlug, scope, workspaceId],
  )
  useEffect(() => {
    if (
      inFlight.current ||
      pending ||
      failed ||
      locked ||
      notebook.locked ||
      !queue.length
    ) {
      return
    }
    const [next, ...rest] = queue
    setQueue(rest)
    void run(next)
  }, [queue, pending, failed, locked, notebook.locked, run])
  const send = useCallback(() => {
    const text = message.trim()
    if (!text) {
      return
    }
    setQueue((value) => [...value, text])
    setMessage("")
  }, [message])
  return {
    message,
    setMessage,
    pending,
    error,
    queue,
    failed,
    send,
    removeQueued: (index: number) =>
      setQueue((value) => value.filter((_, i) => i !== index)),
    retry: () => {
      if (!failed) {
        return
      }
      setQueue((value) => [failed, ...value])
      setFailed("")
      setError("")
    },
    dismissFailure: () => {
      setFailed("")
      setError("")
    },
  }
}
